import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, User, CreditCard, Zap } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { formatPrice } from "@/utils/formatPrice";

// Matches get-public-landing edge function response for training
interface TrainingEntity {
  session_date: string;
  start_time: string;
  duration_minutes: number;
  status: string;
  player_fee: number | null;
  court_name: string | null;
  coach: { full_name: string; avatar_url: string | null } | null;
  group: { name: string; level: string; max_players: number; age_category: string } | null;
  attendance_count: number;
  spots_left: number | null;
}

interface Props {
  entity: Record<string, unknown>;
  onAction: (path: string) => void;
  brandColor?: string;
}

export default function TrainingLanding({ entity, onAction, brandColor }: Props) {
  const session = entity as unknown as TrainingEntity;
  const sessionDate = parseISO(session.session_date);
  const spotsLeft = session.spots_left;
  const maxSpots = session.group?.max_players ?? null;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{session.group?.name ?? "Träning"}</h1>
        <div className="flex flex-wrap gap-2">
          {session.group?.level && (
            <Badge variant="secondary" className="gap-1">
              <Zap className="h-3 w-3" />
              {session.group.level}
            </Badge>
          )}
          {session.group?.age_category && (
            <Badge variant="outline">{session.group.age_category}</Badge>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          {session.coach?.full_name && (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span>Tränare: {session.coach.full_name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{format(sessionDate, "EEEE d MMMM yyyy", { locale: sv })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {session.start_time.slice(0, 5)} ({session.duration_minutes} min)
            </span>
          </div>
          {session.court_name && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{session.court_name}</span>
            </div>
          )}
          {maxSpots != null && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>
                {session.attendance_count}/{maxSpots} platser
                {spotsLeft != null && spotsLeft > 0 && (
                  <span className="text-muted-foreground"> ({spotsLeft} kvar)</span>
                )}
              </span>
            </div>
          )}
          {session.player_fee != null && session.player_fee > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span>{formatPrice(session.player_fee)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action */}
      <Button
        className="w-full text-base py-6"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        disabled={session.status !== "scheduled" || (spotsLeft != null && spotsLeft <= 0)}
        onClick={() => onAction("/training")}
      >
        {spotsLeft != null && spotsLeft <= 0
          ? "Fullbokat"
          : "Boka plats →"}
      </Button>
    </div>
  );
}
